import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Icon } from "@/lib/ui";
import { Loader2 } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function AdminGenerate() {
  const [cats, setCats] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/admin/categories").then((r) => { setCats(r.data.data); }); }, []);

  const generate = async () => {
    if (!categoryId) return toast.error("Select a category");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/tags/generate", { category_id: categoryId, quantity: Number(qty) });
      setResult(data.data);
      toast.success(data.message);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-display font-black text-3xl tracking-tight">Generate Tags</h1>
        <p className="text-muted-foreground mt-1">Create single or bulk QR tags with unique tokens &amp; auth codes.</p>
      </div>

      <div className="rounded-2xl border border-border bg-background p-6 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <span className="text-sm font-semibold">Category</span>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger className="mt-1.5 h-12 rounded-xl" data-testid="gen-category"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <span className="text-sm font-semibold">Quantity</span>
            <input data-testid="gen-quantity" type="number" min={1} max={2000} value={qty} onChange={(e) => setQty(e.target.value)} className="mt-1.5 w-full h-12 px-4 rounded-xl border border-input focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {[1, 10, 50, 100, 500].map((n) => (
            <button key={n} onClick={() => setQty(n)} className="tap px-4 py-2 rounded-full border border-border text-sm font-semibold hover:bg-secondary">{n === 1 ? "Single" : n}</button>
          ))}
        </div>
        <button data-testid="gen-submit" onClick={generate} disabled={busy} className="tap h-13 py-3.5 px-8 rounded-full bg-primary text-primary-foreground font-bold flex items-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Icon name="Sparkles" className="w-5 h-5" />} Generate {qty} tag{qty > 1 ? "s" : ""}
        </button>
      </div>

      {result && (
        <div className="rounded-2xl border border-border bg-background overflow-hidden" data-testid="gen-result">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between">
            <p className="font-display font-bold">{result.length} tag(s) generated</p>
            <button onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/export/tags`, "_blank")} className="tap text-sm font-semibold text-status-active flex items-center gap-1.5"><Icon name="Download" className="w-4 h-4" /> Export all CSV</button>
          </div>
          <div className="overflow-x-auto max-h-[420px]">
            <table className="w-full text-sm">
              <thead className="bg-secondary/60 text-muted-foreground sticky top-0"><tr>
                {["Tag Number", "Auth Code", "Scan URL"].map((h) => <th key={h} className="text-left font-semibold px-4 py-2.5">{h}</th>)}
              </tr></thead>
              <tbody>
                {result.map((t) => (
                  <tr key={t.id} className="border-t border-border">
                    <td className="px-4 py-2.5 font-mono font-semibold whitespace-nowrap">{t.tag_number}</td>
                    <td className="px-4 py-2.5 font-mono">{t.auth_code}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground truncate max-w-[240px]">{t.scan_url}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
